/**
 * MessagesPage — firm-side Client Portal message center at `/messages`
 *
 * Two-pane layout (Slack-style):
 *  - Left: list of clients who have messaged us, with unread count, last
 *    message preview, last activity time
 *  - Right: full message thread with reply composer
 *
 * Backend endpoints:
 *  - GET  /api/client-portal/workspace-messages?workspace_id=…
 *  - GET  /api/client-portal/workspace-messages/{contact_id}?workspace_id=…
 *  - POST /api/client-portal/workspace-messages/{contact_id}?workspace_id=…
 *
 * Why not real-time websockets in MVP: portals are low-frequency. Polling
 * every 15s when the page is visible is plenty — and the inbox preview
 * relies on the same fetcher that updates unread counts.
 */
import { useEffect, useState, useRef, useCallback } from 'react';
import { MessageCircle, Send, ChevronLeft, Inbox, User } from 'lucide-react';
import { useWorkspace } from '../context/WorkspaceContext';
import AppLayout from '../components/layout/AppLayout';
import { apiFetch } from '../fetchInterceptor';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatRelative(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return d.toLocaleDateString();
}

export default function MessagesPage({ embedded = false }) {
  const { currentWorkspace } = useWorkspace();
  const wsId = currentWorkspace?.workspace_id;
  const [threads, setThreads] = useState([]);
  const [activeContactId, setActiveContactId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [activeContact, setActiveContact] = useState(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingThreads, setLoadingThreads] = useState(true);
  const messagesEndRef = useRef(null);

  // Load thread list — also doubles as the unread-count poller
  const loadThreads = useCallback(async () => {
    if (!wsId) return;
    try {
      const r = await apiFetch(`${API_URL}/api/client-portal/workspace-messages?workspace_id=${wsId}`);
      if (r.ok) {
        const j = await r.json();
        setThreads(j.threads || []);
      }
    } finally {
      setLoadingThreads(false);
    }
  }, [wsId]);

  // Load a specific thread
  const loadThread = useCallback(async (contactId) => {
    if (!wsId || !contactId) return;
    const r = await apiFetch(`${API_URL}/api/client-portal/workspace-messages/${contactId}?workspace_id=${wsId}`);
    if (r.ok) {
      const j = await r.json();
      setMessages(j.messages || []);
      setActiveContact(j.contact || null);
      // Optimistically zero out the unread count on the thread we just opened
      setThreads((prev) => prev.map((t) => t.contact_id === contactId ? { ...t, unread: 0 } : t));
    }
  }, [wsId]);

  useEffect(() => { loadThreads(); }, [loadThreads]);

  // Poll every 15s for new messages / threads while the tab is visible
  useEffect(() => {
    if (!wsId) return;
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        loadThreads();
        if (activeContactId) loadThread(activeContactId);
      }
    }, 15000);
    return () => clearInterval(interval);
  }, [wsId, activeContactId, loadThreads, loadThread]);

  useEffect(() => {
    if (activeContactId) loadThread(activeContactId);
  }, [activeContactId, loadThread]);

  // Scroll to bottom on new messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleSend = async () => {
    if (!reply.trim() || !activeContactId) return;
    setSending(true);
    try {
      const r = await apiFetch(
        `${API_URL}/api/client-portal/workspace-messages/${activeContactId}?workspace_id=${wsId}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: reply.trim() }),
        },
      );
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        toast.error(j.detail || 'Send failed');
        return;
      }
      setReply('');
      await loadThread(activeContactId);
      await loadThreads();
    } finally {
      setSending(false);
    }
  };

  // Keyboard shortcut: Ctrl/Cmd+Enter to send
  const onKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSend();
    }
  };

  const totalUnread = threads.reduce((s, t) => s + (t.unread || 0), 0);

  // When embedded inside the unified Inbox we skip the AppLayout wrapper
  // (the parent already renders one). The inner two-pane shell stays.
  const inner = (
    <div className="-m-3 sm:-m-4 lg:-m-6 h-[calc(100vh-4rem)] flex bg-white dark:bg-zinc-950" data-testid="messages-page">
      {/* ── Thread list ────────────────────────────────────── */}
        <aside
          className={cn(
            'w-full sm:w-80 border-r flex flex-col',
            activeContactId && 'hidden sm:flex',
          )}
        >
          <div className="p-4 border-b">
            <div className="flex items-center gap-2">
              <Inbox className="w-5 h-5 text-emerald-600" />
              <h1 className="font-bold text-lg">Messages</h1>
              {totalUnread > 0 && (
                <span className="ml-auto px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-600 text-white">
                  {totalUnread}
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              From your client portal at <code>/c/{currentWorkspace?.slug || 'your-slug'}</code>
            </p>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loadingThreads ? (
              <div className="p-4 space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-16 bg-muted animate-pulse rounded-lg" />
                ))}
              </div>
            ) : threads.length === 0 ? (
              <div className="p-8 text-center" data-testid="messages-empty">
                <MessageCircle className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                <p className="text-sm font-medium">No messages yet</p>
                <p className="text-xs text-muted-foreground mt-1">
                  When a client portal user sends you a message, it'll appear here.
                </p>
              </div>
            ) : (
              <div className="divide-y">
                {threads.map((t) => (
                  <button
                    key={t.contact_id}
                    onClick={() => setActiveContactId(t.contact_id)}
                    className={cn(
                      'w-full text-left p-4 hover:bg-accent/40 transition-colors',
                      activeContactId === t.contact_id && 'bg-accent/60',
                    )}
                    data-testid={`thread-${t.contact_id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 font-semibold flex items-center justify-center flex-shrink-0">
                        {(t.contact_name || '?').charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-medium text-sm truncate">{t.contact_name}</p>
                          <span className="text-[10px] text-muted-foreground flex-shrink-0">
                            {formatRelative(t.last_at)}
                          </span>
                        </div>
                        <div className="flex items-start justify-between gap-2 mt-0.5">
                          <p className="text-xs text-muted-foreground truncate flex-1">
                            {t.last_message || '—'}
                          </p>
                          {t.unread > 0 && (
                            <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-600 text-white flex-shrink-0">
                              {t.unread}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </aside>

        {/* ── Thread view ───────────────────────────────────── */}
        <section
          className={cn(
            'flex-1 flex flex-col',
            !activeContactId && 'hidden sm:flex',
          )}
        >
          {activeContactId && activeContact ? (
            <>
              <header className="p-4 border-b flex items-center gap-3 bg-white/60 dark:bg-zinc-950/60">
                <button
                  type="button"
                  onClick={() => setActiveContactId(null)}
                  className="sm:hidden p-1 hover:bg-accent rounded"
                  data-testid="thread-back"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <div className="w-10 h-10 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 font-semibold flex items-center justify-center">
                  {(activeContact.name || '?').charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold">{activeContact.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{activeContact.email}</p>
                </div>
              </header>

              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {messages.length === 0 ? (
                  <p className="text-center text-sm text-muted-foreground py-12">
                    No messages yet — send the first one below.
                  </p>
                ) : (
                  messages.map((m) => {
                    const fromUs = m.sender !== 'client';
                    return (
                      <div
                        key={m.message_id}
                        className={cn('flex', fromUs ? 'justify-end' : 'justify-start')}
                      >
                        <div
                          className={cn(
                            'max-w-[80%] rounded-2xl px-4 py-2.5',
                            fromUs
                              ? 'bg-emerald-600 text-white rounded-br-sm'
                              : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 rounded-bl-sm',
                          )}
                        >
                          <p className="text-sm whitespace-pre-wrap break-words">{m.text}</p>
                          <p
                            className={cn(
                              'text-[10px] mt-1',
                              fromUs ? 'text-emerald-100' : 'text-zinc-500',
                            )}
                          >
                            {m.sender_name || (fromUs ? 'You' : activeContact.name)} · {formatRelative(m.created_at)}
                          </p>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              <div className="p-4 border-t bg-white dark:bg-zinc-950">
                <Textarea
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder={`Reply to ${activeContact.name}…  (Cmd/Ctrl+Enter to send)`}
                  rows={3}
                  className="resize-none"
                  data-testid="reply-textarea"
                />
                <div className="flex justify-between items-center mt-2">
                  <p className="text-[11px] text-muted-foreground">
                    They'll get an email notification when they're online next.
                  </p>
                  <Button
                    onClick={handleSend}
                    disabled={sending || !reply.trim()}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="reply-send"
                  >
                    {sending ? 'Sending…' : (<>Send<Send className="w-4 h-4 ml-1.5" /></>)}
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground" data-testid="thread-empty">
              <User className="w-12 h-12 mb-3" />
              <p className="text-sm">Pick a conversation to start replying</p>
            </div>
          )}
        </section>
      </div>
  );
  return embedded ? inner : <AppLayout>{inner}</AppLayout>;
}
